import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { CategorySelectFieldProps } from '@/types/predefined-messages';

import { Button } from '@/components/ui/button';
import { FieldLabel } from '@/components/ui/field';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  PREDEFINED_MESSAGE_FORM_FILTER_SURFACE_CLASS,
  usePredefinedMessageFormHelpers,
} from '@/lib/helpers/predefined-messages';
import { cn } from '@/lib/utils';

export function CategorySelectField({
  control,
  categories,
  categoryError,
}: CategorySelectFieldProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const predefinedMessageCategoryId = useWatch({ control, name: 'predefinedMessageCategoryId' });
  const { getCategoryDisplayForPredefinedMessage } = usePredefinedMessageFormHelpers();
  const categoryDisplay = getCategoryDisplayForPredefinedMessage({
    categories,
    predefinedMessageCategoryId,
  });

  return (
    <div className="space-y-2">
      <FieldLabel className="text-sm font-medium" required>
        {t('configuration.predefinedMessages.columns.category')}
      </FieldLabel>
      <Controller
        name="predefinedMessageCategoryId"
        control={control}
        rules={{ required: t('configuration.predefinedMessages.form.selectCategory') }}
        render={({ field: { value, onChange } }) => (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                aria-invalid={categoryError ? 'true' : undefined}
                className={cn(
                  'hover:bg-muted h-9 w-full justify-between px-3 font-normal',
                  PREDEFINED_MESSAGE_FORM_FILTER_SURFACE_CLASS
                )}
              >
                <span
                  className={cn(
                    'min-w-0 flex-1 truncate text-left',
                    categoryDisplay.isPlaceholder && 'text-muted-foreground'
                  )}
                >
                  {categoryDisplay.label}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className={cn(
                'w-full min-w-(--radix-popover-trigger-width) p-0',
                PREDEFINED_MESSAGE_FORM_FILTER_SURFACE_CLASS
              )}
              onOpenAutoFocus={(event) => event.preventDefault()}
              onWheel={(event) => event.stopPropagation()}
            >
              <div
                className="flex max-h-64 flex-col overflow-auto p-1"
                role="listbox"
                aria-label={t('configuration.predefinedMessages.columns.category')}
              >
                {categories.map(({ id, name }) => (
                  <Button
                    key={id}
                    type="button"
                    role="option"
                    variant="ghost"
                    aria-selected={value === id}
                    className={cn(
                      'hover:bg-muted h-auto w-full justify-start px-2 py-1.5 text-sm font-normal'
                    )}
                    onClick={() => {
                      onChange(id);
                      setOpen(false);
                    }}
                  >
                    {name}
                  </Button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
      />
      {categoryError && (
        <p className="text-destructive text-xs" role="alert">
          {categoryError.message}
        </p>
      )}
    </div>
  );
}
